import { Fragment } from 'react';

import { RangePicker } from '@/entities/range-picker';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import { Input } from '@/shared/ui/input';
import { Multiselect } from '@/shared/ui/multiselect';
import { Select } from '@/shared/ui/select';
import { Tooltip } from '@/shared/ui/tooltip';

import { FILTER_TYPE, type FilterType } from './table-filters.constants';
import * as Styled from './table-filters.styles';
import { DateRangeValue, FilterRendererContext, FilterRendererMap, TableFilter } from './table-filters.types';

const FILTER_RENDERERS: FilterRendererMap = {
	[FILTER_TYPE.SELECT]: (filter, { onChange }) => {
		const handleChange = filter.onChange ?? ((value: string) => onChange?.(filter.id, value));
		const select = (
			<Select
				label={filter.label}
				options={filter.options}
				selected={filter.value}
				emptyValueText={filter.emptyValueText}
				disabled={filter.disabled}
				required={filter.required}
				onChange={handleChange}
				{...filter.selectProps}
			/>
		);

		if (filter.disabled && filter.disabledTooltip) {
			return (
				<Styled.SelectBox key={filter.id}>
					<Tooltip
						tooltip={filter.disabledTooltip}
						direction="bottom"
						position="absolute"
						width="100%"
						boxSizing="border-box"
					>
						{select}
					</Tooltip>
				</Styled.SelectBox>
			);
		}

		return <Styled.SelectBox key={filter.id}>{select}</Styled.SelectBox>;
	},
	[FILTER_TYPE.DATE_RANGE]: (filter, { onChange }) => {
		const range = filter.value;
		const startLabel = filter.startLabel ?? filter.label ?? '';
		const endLabel = filter.endLabel ?? filter.label ?? '';
		const handleChange = filter.onChange ?? ((value: DateRangeValue) => onChange?.(filter.id, value));

		return (
			<RangePicker
				key={filter.id}
				startDate={range.startDate}
				endDate={range.endDate}
				startDateLabel={startLabel}
				endDateLabel={endLabel}
				onChangeStartDate={(startDate) => handleChange({ ...range, startDate })}
				onChangeEndDate={(endDate) => handleChange({ ...range, endDate })}
				required={filter.required}
				{...filter.rangeProps}
			/>
		);
	},
	[FILTER_TYPE.INPUT]: (filter, { onChange }) => {
		const handleChange = filter.onChange ?? ((nextValue: string) => onChange?.(filter.id, nextValue));

		return (
			<Input
				key={filter.id}
				label={filter.label}
				placeholder={filter.placeholder}
				value={filter.value}
				disabled={filter.disabled}
				required={filter.required}
				onChange={handleChange}
				{...filter.inputProps}
			/>
		);
	},
	[FILTER_TYPE.CHECKBOX]: (filter, { onChange }) => {
		const handleChange = (nextValue: boolean) => {
			if (filter.onChange) {
				filter.onChange(nextValue);

				return;
			}

			onChange?.(filter.id, nextValue);
		};

		return (
			<Checkbox
				key={filter.id}
				checked={filter.value}
				text={filter.text ?? filter.label}
				disabled={filter.disabled}
				onChange={handleChange}
				{...filter.checkboxProps}
			/>
		);
	},
	[FILTER_TYPE.MULTISELECT]: (filter, { onChange }) => {
		const selectedValues = filter.values ?? [];
		const handleChange = filter.onChange ?? ((nextValues: string[]) => onChange?.(filter.id, nextValues));
		const setAll = filter.setAll ?? (() => {});
		const isAll = filter.isAll ?? false;

		return (
			<Multiselect
				key={filter.id}
				options={filter.options}
				values={selectedValues}
				setData={handleChange}
				setAll={setAll}
				isAll={isAll}
				label={filter.label}
				placeholder={filter.placeholder}
				searchPlaceholder={filter.searchPlaceholder}
				width={filter.width}
				disabled={filter.disabled}
				required={filter.required}
				{...filter.multiselectProps}
			/>
		);
	},
	[FILTER_TYPE.BUTTON]: (filter) => (
		<Button
			key={filter.id}
			label={filter.label}
			onClick={filter.onClick}
			disabled={filter.disabled}
			{...filter.buttonProps}
		/>
	),
	[FILTER_TYPE.CUSTOM]: (filter) => <Fragment key={filter.id}>{filter.render()}</Fragment>,
};

export const renderFilter = (filter: TableFilter, context: FilterRendererContext) => {
	const renderer = FILTER_RENDERERS[filter.type as FilterType] as (
		filter: TableFilter,
		context: FilterRendererContext,
	) => React.JSX.Element | null;

	return renderer(filter, context);
};
